/**
 * Structured Data Component
 * Adds JSON-LD structured data for better search engine understanding
 * Helps with rich snippets and search result enhancements
 */

import { useEffect } from 'react'

interface OrganizationSchemaProps {
  type: 'organization'
}

interface WebsiteSchemaProps {
  type: 'website'
}

interface BreadcrumbSchemaProps {
  type: 'breadcrumb'
  items: Array<{
    name: string
    url: string
  }>
}

interface ItemListSchemaProps {
  type: 'itemList'
  name: string
  description?: string
  items: Array<{
    name: string
    description?: string
    url?: string
    image?: string
  }>
}

interface FAQSchemaProps {
  type: 'faq'
  questions: Array<{
    question: string
    answer: string
  }>
}

interface SoftwareApplicationSchemaProps {
  type: 'softwareApplication'
}

type StructuredDataProps =
  | OrganizationSchemaProps
  | WebsiteSchemaProps
  | BreadcrumbSchemaProps
  | ItemListSchemaProps
  | FAQSchemaProps
  | SoftwareApplicationSchemaProps

export const StructuredData = (props: StructuredDataProps) => {
  useEffect(() => {
    const scriptId = `structured-data-${props.type}`
    let existingScript = document.getElementById(scriptId)

    // Remove existing script if it exists
    if (existingScript) {
      existingScript.remove()
    }

    // Create new script
    const script = document.createElement('script')
    script.id = scriptId
    script.type = 'application/ld+json'
    script.text = JSON.stringify(getSchemaData(props), null, 2)
    document.head.appendChild(script)

    return () => {
      const scriptToRemove = document.getElementById(scriptId)
      if (scriptToRemove) {
        scriptToRemove.remove()
      }
    }
  }, [props])

  return null
}

/**
 * Generate appropriate schema data based on type
 */
function getSchemaData(props: StructuredDataProps): object {
  switch (props.type) {
    case 'organization':
      return {
        '@context': 'https://schema.org',
        '@type': 'Organization',
        name: 'Paiseh!',
        alternateName: 'Paiseh Wishlist',
        url: 'https://paiseh.sg',
        logo: 'https://paiseh.sg/logo.png',
        description: 'Free online wishlist maker and gift registry for Singapore. Create wishlists for birthdays, weddings, Christmas, baby showers and more.',
        address: {
          '@type': 'PostalAddress',
          addressCountry: 'SG',
        },
        areaServed: 'SG',
        sameAs: [],
      }

    case 'website':
      return {
        '@context': 'https://schema.org',
        '@type': 'WebSite',
        name: 'Paiseh! - Free Online Wishlist Maker',
        alternateName: 'Paiseh Wishlist Singapore',
        url: 'https://paiseh.sg',
        description:
          'Create free wishlists online and share with family & friends. Best wishlist app in Singapore for birthdays, Christmas, weddings & baby showers. Smart reservation system prevents duplicate gifts.',
        inLanguage: 'en-SG',
        potentialAction: {
          '@type': 'SearchAction',
          target: {
            '@type': 'EntryPoint',
            urlTemplate: 'https://paiseh.sg/search?q={search_term_string}',
          },
          'query-input': 'required name=search_term_string',
        },
      }

    case 'breadcrumb':
      return {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: props.items.map((item, index) => ({
          '@type': 'ListItem',
          position: index + 1,
          name: item.name,
          item: item.url,
        })),
      }

    case 'itemList':
      return {
        '@context': 'https://schema.org',
        '@type': 'ItemList',
        name: props.name,
        description: props.description,
        numberOfItems: props.items.length,
        itemListElement: props.items.map((item, index) => ({
          '@type': 'ListItem',
          position: index + 1,
          name: item.name,
          description: item.description,
          url: item.url,
          image: item.image,
        })),
      }

    case 'faq':
      return {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: props.questions.map((q) => ({
          '@type': 'Question',
          name: q.question,
          acceptedAnswer: {
            '@type': 'Answer',
            text: q.answer,
          },
        })),
      }

    case 'softwareApplication':
      return {
        '@context': 'https://schema.org',
        '@type': 'SoftwareApplication',
        name: 'Paiseh!',
        description: 'Free online wishlist maker and gift registry for Singapore. Create and share wishlists for birthdays, weddings, Christmas, and more.',
        url: 'https://paiseh.sg',
        applicationCategory: 'LifestyleApplication',
        operatingSystem: 'Web',
        offers: {
          '@type': 'Offer',
          price: '0',
          priceCurrency: 'SGD',
        },
        aggregateRating: {
          '@type': 'AggregateRating',
          ratingValue: '4.8',
          ratingCount: '150',
          bestRating: '5',
          worstRating: '1',
        },
        featureList: [
          'Create unlimited wishlists',
          'Share wishlists with friends and family',
          'Gift reservation system prevents duplicates',
          'Secret Santa organizer',
          'No sign-up required',
          'Free to use',
        ],
      }

    default:
      return {}
  }
}
